import { useFela } from 'react-fela';
import { FormattedMessage, useIntl } from 'react-intl';

import { RoleDropdown, type RoleDropdownProps } from '@/modules/forms';
import { IconButton, Text } from '@/modules/ui';
import { permissionByRole, permissionToRole } from '@/utilities/permission';

import type { GroupWithPermissions } from '../../types';
import { GroupDetailButton } from '../GroupDetailButton';
import * as felaRules from './GroupItem.rules';

export interface GroupItemProps extends GroupWithPermissions {
    onRemove?: (id: GroupWithPermissions['id']) => void;
    onUpdate?: (item: GroupWithPermissions) => void;
    editable?: boolean;
    hasPasskey: boolean;
    roles?: RoleDropdownProps['roles'];
}

export const GroupItem = ({ id, name, email, accessPermissions, editable, onUpdate, onRemove, hasPasskey, roles }: GroupItemProps) => {
    const { css } = useFela();

    const { formatMessage } = useIntl();

    const accessRole = permissionToRole(accessPermissions);

    return (
        <li className={css(felaRules.container)}>
            <GroupDetailButton id={id} name={name} />
            {editable ? (
                <div className={css(felaRules.editOptionsContainer)}>
                    <RoleDropdown
                        value={accessRole}
                        hasPasskey={hasPasskey}
                        roles={roles}
                        onChange={newRole => {
                            onUpdate?.({
                                id,
                                name,
                                email,
                                accessPermissions: permissionByRole[newRole],
                            });
                        }}
                    />
                    <IconButton
                        aria-label={formatMessage({ id: 'remove' })}
                        type='ghost'
                        icon='trash'
                        customStyle={felaRules.trashButton}
                        onClick={() => {
                            onRemove?.(id);
                        }}
                    />
                </div>
            ) : (
                <Text variant='ParagraphSmallMedium' color='foregroundsTertiary' customStyle={felaRules.roleText}>
                    <FormattedMessage id={`secret.permission.${accessRole}`} />
                </Text>
            )}
        </li>
    );
};
